/**
 * Moving a `problem+json` document into a form — §7.2, roadmap 4.2.
 *
 * The API is the authority on validity: it knows that a handle is taken, that a
 * paid course needs a price, and that this email already has an account. Zod
 * catches what can be checked without asking, and everything else comes back as
 * `errors[]` — which this turns into `react-hook-form` errors on the matching
 * inputs.
 *
 * The case that decides whether this is worth a module: a field error for an
 * input the form does not render. Dropping it means the user presses Save,
 * nothing moves, and there is no message anywhere. Folding it into the
 * form-level message is worse than perfect and far better than silence.
 */
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form'

import { isApiError, SYNTHESISED_PROBLEM_CODE } from '@/shared/api/problem'

/**
 * The message to show for a failed mutation on a surface that is not a form.
 *
 * A list row's Delete, a confirm dialog's button, an inbox's Accept — none of
 * them has a `react-hook-form` to route field errors into, and each was
 * reinventing this line. Three ways of getting it wrong were in the codebase:
 *
 * 1. `catch {}` — the error vanished and the user saw nothing at all.
 * 2. A local string *instead of* the server's message, so a precise refusal
 *    ("that weekday already has hours") was replaced by a vague one.
 * 3. ⚠️ **`error.problem.detail ?? fallback`** — which looks right and is not.
 *    `detail` is a non-optional `string`, so `??` never fires; a response that
 *    is not a problem document at all (nginx's 502, a captive portal) has
 *    `detail: ''` and rendered an **empty** error. It also discarded the
 *    unattached `errors[]` entries that `formError()` joins.
 *
 * `formError()` already layers correctly — unattached errors, then `detail`,
 * then the title — so `fallback` is reached only when the response carried no
 * usable text whatsoever.
 *
 * The server's `detail` arrives already translated, resolved from the caller's
 * `Accept-Language`, so it is rendered as-is and never wrapped in a local
 * sentence.
 *
 * ⚠️ **A fourth way of getting it wrong, fixed here: `formError()` returning a
 * sentence `toApiError` made up.** A response that is not a problem document
 * has no server prose, so `toApiError` puts the translated "unexpected
 * response" in `title` to stop a bare `formError()` rendering blank — and that
 * sentence is truthy, so `|| fallback` stopped firing and every caller's own
 * string became dead code. `t('upload.failed')` and `t('wizard.photos.failed')`
 * were unreachable on exactly the 413/502 path they were written for.
 *
 * So a synthesised document is recognised by its `code` and the caller's string
 * wins. That is the right way round: the caller knows a photograph was being
 * uploaded, and the generic cannot. The generic stays the floor for the screens
 * that call `formError()` with no fallback at all, where it is the only thing
 * standing between the user and an empty box.
 */
export function messageFor(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    if (error.code === SYNTHESISED_PROBLEM_CODE) return fallback || error.formError()
    return error.formError() || fallback
  }
  return (error instanceof Error && error.message) || fallback
}

export type AppliedProblem = {
  /** Message to show above the form, if any. */
  formError: string | null
  /** Fields that were set — mostly for tests. */
  handledFields: string[]
}

/**
 * Apply an error from a mutation to a form.
 *
 * Returns the message that belongs above the form: the problem's unattached
 * errors, plus any field error whose field is not in this form.
 */
export function applyProblemErrors<T extends FieldValues>(
  error: unknown,
  form: UseFormReturn<T>,
  /**
   * The fields this form actually renders. Anything outside it is reported at
   * form level rather than sent to `setError`, where it would set an error on a
   * field that can never be corrected — and `react-hook-form` would then block
   * submission forever.
   */
  knownFields: readonly Path<T>[],
): AppliedProblem {
  if (!isApiError(error)) {
    return { formError: error instanceof Error ? error.message : null, handledFields: [] }
  }

  const known = new Set<string>(knownFields as readonly string[])
  const handledFields: string[] = []
  const orphaned: string[] = []
  /**
   * Did any input receive a message a human can read?
   *
   * Tracked as we go rather than looked up afterwards: `handledFields` holds
   * the *form's* field name while `fieldErrors()` is keyed by the *server's*
   * (`profile.handle` resolves to `handle`), so re-reading the map by the
   * resolved name misses exactly the nested case.
   */
  let anyFieldHasProse = false

  for (const [field, message] of Object.entries(error.fieldErrors())) {
    // The API names nested fields with a dotted path — `profile.handle` — while
    // a flat form registers `handle`. Try the full path first, since a form
    // that mirrors the nesting should win, then the last segment.
    //
    // Without the fallback every profile error lands in the form-level banner
    // instead of on the input that caused it, which is a silent downgrade: the
    // message still appears, so nothing looks broken.
    const target = known.has(field) ? field : field.split('.').at(-1)

    if (target && known.has(target)) {
      form.setError(target as Path<T>, { type: 'server', message })
      handledFields.push(target)
      if (message.trim()) anyFieldHasProse = true
    } else {
      orphaned.push(message)
    }
  }

  const formLevel = error.formError()

  /**
   * ⚠️ **This condition was turned around by the 22 October 2026 body
   * reformatting**, and it is the one line deciding whether the user can read
   * what went wrong.
   *
   * It used to suppress the form-level message whenever *any* field was
   * handled, because each `errors[]` entry carried its own sentence and
   * repeating them in a banner above was noise. Entries now carry only
   * `{field, code}` and the prose lives solely in the top-level `detail` — so
   * keeping the old rule would outline two inputs and explain neither.
   *
   * The test is "did a readable message reach an input", not "was a field
   * handled": `FIELD_INVALID_NO_MESSAGE` is deliberately a single space, so it
   * highlights without saying anything and the banner must still speak.
   */
  // `orphaned` can hold blank placeholders for fields this form does not
  // render, which would otherwise join into a banner made of spaces.
  const realOrphans = orphaned.filter((message) => message.trim())
  const parts = anyFieldHasProse ? realOrphans : [formLevel, ...realOrphans]

  return {
    formError: parts.filter((part) => part.trim()).join(' ') || null,
    handledFields,
  }
}
